import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { formatCurrency } from '@/lib/utils';
import { useOfferStore } from '@/lib/store';
import { GAEBEntry } from '@shared/types';
import { Position } from '@shared/schema';
import {
  buildGaebEditorSummary,
  getEffectiveGaebQuantity as getEffectiveQuantity,
} from '@shared/gaeb-editor-summary';
import { FileText, Save, ArrowRight, Trash2, Eye, FileDown, Loader2, Settings2, ChevronDown, Lock, Unlock, AlertTriangle } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import GAEBExport from './GAEBExport';
import GaebTeilExport from './GaebTeilExport';
import { TextEditModal } from '@/components/ui/text-edit-modal';

// Edit mode type: X84 = quantities locked (default), X85 = quantities editable
type GAEBEditMode = 'x84' | 'x85';

// Track which fields have been modified
interface ModifiedFields {
  [key: string]: Set<string>; // key = position ID, value = set of modified field names
}

// Helper function to check if an entry is a Bedarfsposition (optional/provisional)
const isBedarfsposition = (entry: GAEBEntry): boolean => {
  return !!(entry as any).isBedarfsposition;
};

// Helper function to check if an entry has TextComplement markers needing bidder input
// These are positions where the original X83 had placeholders for bidder comments
const needsBidderInput = (entry: GAEBEntry): boolean => {
  const structures = (entry as any).gaebTextComplementStructures;
  if (!structures || !Array.isArray(structures)) return false;
  // Check if any marker has Kind="Bidder" (bidder must fill in)
  return structures.some((s: any) => s.kind === 'Bidder');
};

// Helper function to check if an entry has a user comment filled in
const hasUserComment = (entry: GAEBEntry): boolean => {
  return !!(entry.ourComment && entry.ourComment.trim().length > 0);
};

// Helper function to filter out text-align:justify; from long text
const filterLongText = (text: string): string => {
  if (!text) return '';
  return text.replace(/text-align:justify;\s*/g, '').trim();
};

export default function GAEBEditor() {
  const positions = useOfferStore(s => s.positions);
  const gaebMetadata = useOfferStore(s => s.gaebMetadata);
  const currentDatabaseId = useOfferStore(s => s.currentDatabaseId);
  const updateGAEBEntry = useOfferStore((state) => state.updateGAEBEntry);
  const convertGAEBToOffer = useOfferStore((state) => state.convertGAEBToOffer);
  const gaebSummary = useMemo(
    () => buildGaebEditorSummary(positions as unknown as GAEBEntry[]),
    [positions],
  );
  const [modifiedFields, setModifiedFields] = useState<ModifiedFields>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [editableGewerkNames, setEditableGewerkNames] = useState<{ [key: string]: string }>({});
  const [editingGewerkKey, setEditingGewerkKey] = useState<string | null>(null);

  // Edit mode: X84 (default, quantities locked) or X85 (quantities editable)
  const [editMode, setEditMode] = useState<GAEBEditMode>(() => {
    const saved = localStorage.getItem('gaeb-edit-mode');
    return (saved === 'x85') ? 'x85' : 'x84';
  });

  // Mode switch warning dialog
  const [showModeWarning, setShowModeWarning] = useState(false);
  const [pendingModeSwitch, setPendingModeSwitch] = useState<GAEBEditMode | null>(null);

  // Local string values for price inputs (to handle decimal typing)
  const [localPriceInputs, setLocalPriceInputs] = useState<{ [key: number]: string }>({});
  const [localQuantityInputs, setLocalQuantityInputs] = useState<{ [key: number]: string }>({});

  // PDF Export options (persisted in localStorage)
  const [pdfOptions, setPdfOptions] = useState(() => {
    const saved = localStorage.getItem('gaeb-pdf-options');
    return saved ? JSON.parse(saved) : {
      includeComments: true,
      showPrices: true,
      showQuantities: true
    };
  });
  const { toast } = useToast();
  const loadDatabaseData = useOfferStore((state) => state.loadDatabaseData);

  // Calculate modifications info for mode switch warnings
  const modificationsInfo = useMemo(() => {
    const changedQty = positions.filter(p => {
      const gaebEntry = p as any;
      if (gaebEntry.type !== 'item') return false;
      const originalQty = gaebEntry.originalQuantity ?? gaebEntry.menge ?? 1;
      const adjustedQty = gaebEntry.adjustedQuantity;
      return adjustedQty !== undefined && adjustedQty !== originalQty;
    });

    const withComments = positions.filter(p => {
      const gaebEntry = p as any;
      return gaebEntry.type === 'item' && gaebEntry.ourComment && gaebEntry.ourComment.trim();
    });

    return {
      hasChangedQuantities: changedQty.length > 0,
      hasComments: withComments.length > 0,
      hasModifications: changedQty.length > 0 || withComments.length > 0,
      changedQuantitiesCount: changedQty.length,
      commentsCount: withComments.length,
      changedQuantityPositions: changedQty.map(p => (p as any).positionNumber).slice(0, 5)
    };
  }, [positions]);

  // Handle mode switch with warning if modifications exist
  const handleModeSwitch = (newMode: GAEBEditMode) => {
    if (newMode === 'x84' && modificationsInfo.hasModifications) {
      // Switching TO X84 with modifications - show warning
      setPendingModeSwitch(newMode);
      setShowModeWarning(true);
    } else {
      // No warning needed
      confirmModeSwitch(newMode);
    }
  };

  const confirmModeSwitch = (mode: GAEBEditMode) => {
    setEditMode(mode);
    localStorage.setItem('gaeb-edit-mode', mode);
    setShowModeWarning(false);
    setPendingModeSwitch(null);

    toast({
      title: mode === 'x84' ? 'X84 Modus aktiviert' : 'X85 Modus aktiviert',
      description: mode === 'x84'
        ? 'Mengen sind gesperrt. Export als X84.'
        : 'Mengen können angepasst werden. Export als X85.',
      duration: 3000
    });
  };

  // Save PDF options to localStorage
  const updatePdfOption = (key: string, value: boolean) => {
    const newOptions = { ...pdfOptions, [key]: value };
    setPdfOptions(newOptions);
    localStorage.setItem('gaeb-pdf-options', JSON.stringify(newOptions));
  };

  // Direct PDF export handler
  const handleExportPDF = async () => {
    if (!currentDatabaseId || !gaebMetadata) return;

    setIsExportingPDF(true);
    try {
      const response = await fetch('/api/gaeb/export-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          databaseId: currentDatabaseId,
          includeComments: pdfOptions.includeComments,
          showPrices: pdfOptions.showPrices,
          showQuantities: pdfOptions.showQuantities
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'PDF Export fehlgeschlagen' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const baseName = gaebMetadata.originalFilename.replace(/\.(xml|d83|x83|d84|x84|p83|p84)$/i, '');
      link.download = `${baseName}_LV_${timestamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({ title: "PDF exportiert", description: link.download });
    } catch (error) {
      toast({
        title: "PDF Export fehlgeschlagen",
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variant: "destructive",
      });
    } finally {
      setIsExportingPDF(false);
    }
  };

  // Auto-save timer
  useEffect(() => {
    // Set up auto-save when modified fields change
    if (Object.keys(modifiedFields).length > 0) {
      const timer = setTimeout(async () => {
        await saveChanges();
      }, 3000); // Save 3 seconds after last change
      
      return () => clearTimeout(timer);
    }
  }, [modifiedFields]);

  const saveChanges = async () => {
    if (!currentDatabaseId || Object.keys(modifiedFields).length === 0) {
      return;
    }

    try {
      setIsSaving(true);

      const entriesToSave = positions
        .filter(p => modifiedFields[p.id])
        .map(p => {
          const gaebEntry = p as any;
          // CRITICAL: Use null instead of undefined for reset values
          // JSON.stringify drops undefined values, but preserves null
          // null signals "reset to original value"
          const adjustedQty = gaebEntry.adjustedQuantity;
          const price = gaebEntry.ourPrice;
          const comment = gaebEntry.ourComment;
          return {
            // CRITICAL: artikelId must match the database entry's artikelId for updates to work
            // Priority: artikelId (exact match) > originalId (fallback)
            artikelId: gaebEntry.artikelId || p.originalId,
            // If ourPrice is undefined, send null to signal "reset to originalPrice"
            ourPrice: price === undefined ? null : price,
            // If ourComment is undefined/empty, send null to signal "clear comment"
            // JSON.stringify drops undefined, so we must use null for clear signals
            ourComment: (comment === undefined || comment === '') ? null : comment,
            // If adjustedQuantity is undefined, send null to signal "reset to originalQuantity"
            adjustedQuantity: adjustedQty === undefined ? null : adjustedQty
          };
        });

      if (entriesToSave.length === 0) {
        return;
      }

      console.log('Auto-saving', entriesToSave.length, 'entries');

      const response = await fetch('/api/gaeb/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          databaseId: currentDatabaseId,
          entries: entriesToSave
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Save failed' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      console.log('Auto-save successful');
      // Reset modified fields after successful save
      setModifiedFields({});
      
      toast({
        title: "Gespeichert",
        description: `${entriesToSave.length} Änderungen gespeichert`,
        duration: 2000
      });
    } catch (error) {
      console.error('Auto-save error:', error);
      toast({
        title: "Speichern fehlgeschlagen",
        description: error instanceof Error ? error.message : 'Fehler beim Speichern',
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePriceChange = (id: number, price: number | undefined) => {
    updateGAEBEntry(id, { ourPrice: price });

    // Track this field as modified
    setModifiedFields(prev => ({
      ...prev,
      [id]: new Set([...(Array.from(prev[id] || [])), 'ourPrice'])
    }));
  };

  const handleCommentChange = (id: number, comment: string | undefined) => {
    updateGAEBEntry(id, { ourComment: comment });

    // Track this field as modified
    setModifiedFields(prev => ({
      ...prev,
      [id]: new Set([...(Array.from(prev[id] || [])), 'ourComment'])
    }));
  };

  // NEW: Add quantity change handler
  // CRITICAL: This function must preserve quantity integrity
  // - adjustedQuantity should ONLY be set if it differs from originalQuantity
  // - If user resets to original, adjustedQuantity must become undefined
  const handleQuantityChange = (id: number, newQuantity: number | undefined) => {
    const position = positions.find(p => p.id === id);
    if (!position) return;

    const gaebEntry = position as unknown as GAEBEntry;

    // Determine the canonical original quantity (immutable source of truth)
    const originalQty = typeof gaebEntry.originalQuantity === 'number' && Number.isFinite(gaebEntry.originalQuantity)
      ? gaebEntry.originalQuantity
      : (typeof gaebEntry.menge === 'number' && Number.isFinite(gaebEntry.menge) ? gaebEntry.menge : 0);

    // Parse the new value - undefined means user cleared the field
    const parsedQuantity = typeof newQuantity === 'number' && Number.isFinite(newQuantity) ? newQuantity : undefined;

    // CRITICAL DECISION: Is this a change from original or a reset?
    // If parsedQuantity is undefined OR equals originalQty → reset to original (adjustedQuantity = undefined)
    // If parsedQuantity differs from originalQty → set adjustedQuantity
    const isResetToOriginal = parsedQuantity === undefined || parsedQuantity === originalQty;
    const nextAdjustedQuantity = isResetToOriginal ? undefined : parsedQuantity;

    console.log('[QUANTITY DEBUG]', {
      id,
      originalQty,
      parsedQuantity,
      isResetToOriginal,
      nextAdjustedQuantity,
      currentAdjusted: gaebEntry.adjustedQuantity
    });

    let originalComment = gaebEntry.ourComment || '';

    // Remove old quantity adjustment notes but keep other comments
    const otherComments = originalComment
      .split('|')
      .map((c: string) => c.trim())
      .filter((c: string) => !c.includes('Menge angepasst') && !c.includes('dynamically adjusted'))
      .join(' | ')
      .trim();

    let nextComment = otherComments;

    // Only add quantity note if we're actually changing from original
    if (nextAdjustedQuantity !== undefined) {
      const currentAdjustedQty = typeof gaebEntry.adjustedQuantity === 'number' && Number.isFinite(gaebEntry.adjustedQuantity)
        ? gaebEntry.adjustedQuantity
        : originalQty;

      const changeNote = currentAdjustedQty === originalQty
        ? `Menge angepasst: ${originalQty} → ${nextAdjustedQuantity}`
        : `Menge angepasst: ${originalQty} → ${currentAdjustedQty} → ${nextAdjustedQuantity} (dynamically adjusted)`;
      nextComment = otherComments ? `${otherComments} | ${changeNote}` : changeNote;
    }

    // CRITICAL: Pass undefined explicitly to clear adjustedQuantity when resetting
    updateGAEBEntry(id, {
      adjustedQuantity: nextAdjustedQuantity,
      ourComment: nextComment || undefined
    });

    // Track this field as modified
    setModifiedFields(prev => ({
      ...prev,
      [id]: new Set([...(Array.from(prev[id] || [])), 'adjustedQuantity'])
    }));
  };

  const isFieldModified = (id: number, field: string): boolean => {
    return modifiedFields[id]?.has(field) ?? false;
  };

  const getFieldClassName = (id: number, field: string, isTitle: boolean) => {
    const modified = isFieldModified(id, field);
    const baseClass = isTitle ? 'font-semibold bg-orange-50' : '';
    const modifiedClass = modified ? 'text-red-600 font-medium bg-red-50' : '';
    return `${baseClass} ${modifiedClass}`.trim();
  };

  const hasUnsavedChanges = Object.keys(modifiedFields).length > 0;

  // Calculate completion status
  const itemsWithPrices = gaebSummary.pricedItemCount;
  const totalItems = gaebSummary.totalItemCount;

  const completionRate = totalItems > 0 ? Math.round((itemsWithPrices / totalItems) * 100) : 0;
  const isComplete = completionRate === 100;

  return (
    <div className="w-full space-y-4">
      {/* Header with GAEB info */}
      <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <FileText className="h-5 w-5 text-orange-600" />
          <div>
            <h2 className="font-semibold text-orange-900">{gaebMetadata?.projectName || 'Kein Projektname'}</h2>
            <p className="text-sm text-orange-700">{gaebMetadata?.clientName || 'Kein Kunde'}</p>
          </div>
          {/* Edit metadata button */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-orange-600 hover:bg-orange-100">
                <Settings2 className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4" align="start">
              <div className="space-y-4">
                <p className="text-sm font-medium text-gray-700">LV-Metadaten bearbeiten</p>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="meta-project" className="text-xs text-gray-600">Projektname / Bauvorhaben</Label>
                    <Input
                      id="meta-project"
                      value={gaebMetadata?.projectName || ''}
                      onChange={(e) => {
                        const updateGAEBMetadata = useOfferStore.getState().updateGAEBMetadata;
                        updateGAEBMetadata({ projectName: e.target.value });
                      }}
                      onBlur={async (e) => {
                        // Save metadata to server on blur
                        if (currentDatabaseId) {
                          try {
                            await fetch('/api/gaeb/save', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                databaseId: currentDatabaseId,
                                entries: [],
                                metadata: { projectName: e.target.value }
                              })
                            });
                          } catch (err) {
                            console.error('Failed to save metadata:', err);
                          }
                        }
                      }}
                      placeholder="Projektname eingeben..."
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="meta-client" className="text-xs text-gray-600">Kunde / Auftraggeber</Label>
                    <Input
                      id="meta-client"
                      value={gaebMetadata?.clientName || ''}
                      onChange={(e) => {
                        const updateGAEBMetadata = useOfferStore.getState().updateGAEBMetadata;
                        updateGAEBMetadata({ clientName: e.target.value });
                      }}
                      onBlur={async (e) => {
                        // Save metadata to server on blur
                        if (currentDatabaseId) {
                          try {
                            await fetch('/api/gaeb/save', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                databaseId: currentDatabaseId,
                                entries: [],
                                metadata: { clientName: e.target.value }
                              })
                            });
                          } catch (err) {
                            console.error('Failed to save metadata:', err);
                          }
                        }
                      }}
                      placeholder="Kundenname eingeben..."
                      className="mt-1"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Diese Daten werden beim X84/X85 Export verwendet.
                </p>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex items-center space-x-2">
          {hasUnsavedChanges && (
            <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 border-yellow-300">
              {Object.keys(modifiedFields).length} ungespeichert
            </Badge>
          )}
          {!isComplete && (
            <Badge
              variant="secondary"
              className="bg-blue-100 text-blue-800 border-blue-300 animate-pulse"
            >
              <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              {completionRate}% bepreist
            </Badge>
          )}
          {isComplete && (
            <Badge className="bg-green-600 text-white">
              ✓ Vollständig
            </Badge>
          )}

          {/* Mode Toggle: X84 (locked quantities) vs X85 (editable quantities) */}
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-1.5">
            <div className={`flex items-center gap-1 text-xs font-medium ${editMode === 'x84' ? 'text-blue-700' : 'text-gray-400'}`}>
              <Lock className="h-3.5 w-3.5" />
              X84
            </div>
            <Switch
              checked={editMode === 'x85'}
              onCheckedChange={(checked) => handleModeSwitch(checked ? 'x85' : 'x84')}
              className="data-[state=checked]:bg-amber-500"
            />
            <div className={`flex items-center gap-1 text-xs font-medium ${editMode === 'x85' ? 'text-amber-700' : 'text-gray-400'}`}>
              X85
              <Unlock className="h-3.5 w-3.5" />
            </div>
          </div>

          <Badge className={editMode === 'x84' ? 'bg-blue-600' : 'bg-amber-600'}>
            {editMode === 'x84' ? 'X84 Modus' : 'X85 Modus'}
          </Badge>
        </div>
      </div>

      {/* Action buttons and legend */}
      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
        <div className="flex items-center space-x-4 text-xs text-gray-600">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-orange-50 border border-orange-200 rounded"></div>
            <span>Titel (Title)</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-red-50 border border-red-200 rounded"></div>
            <span className="text-red-600">Geändert (Modified)</span>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <GAEBExport />
          <GaebTeilExport />
          {/* PDF Export with inline options */}
          <div className="flex items-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPDF}
              disabled={isExportingPDF}
              className="text-blue-600 border-blue-300 hover:bg-blue-50 rounded-r-none border-r-0"
              title="X83-LV als PDF exportieren"
            >
              {isExportingPDF ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-1" />
              )}
              PDF
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-blue-600 border-blue-300 hover:bg-blue-50 rounded-l-none px-1.5"
                  disabled={isExportingPDF}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="end">
                <div className="space-y-3">
                  <p className="text-xs font-medium text-gray-700">PDF Optionen</p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="pdf-comments"
                        checked={pdfOptions.includeComments}
                        onCheckedChange={(checked) => updatePdfOption('includeComments', !!checked)}
                      />
                      <Label htmlFor="pdf-comments" className="text-xs cursor-pointer">
                        Kommentare (rot)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="pdf-prices"
                        checked={pdfOptions.showPrices}
                        onCheckedChange={(checked) => updatePdfOption('showPrices', !!checked)}
                      />
                      <Label htmlFor="pdf-prices" className="text-xs cursor-pointer">
                        Preise anzeigen
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="pdf-quantities"
                        checked={pdfOptions.showQuantities}
                        onCheckedChange={(checked) => updatePdfOption('showQuantities', !!checked)}
                      />
                      <Label htmlFor="pdf-quantities" className="text-xs cursor-pointer">
                        Mengen anzeigen
                      </Label>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <Button
            onClick={() => {
              if (hasUnsavedChanges) {
                toast({
                  title: "Ungespeicherte Änderungen",
                  description: "Bitte speichern Sie zuerst Ihre Änderungen (automatisch in 3 Sekunden)",
                  variant: "destructive",
                });
                return;
              }
              convertGAEBToOffer();
              toast({
                title: "Übernommen",
                description: "X83-Daten wurden in Preisliste übernommen",
              });
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="button-convert-gaeb-to-offer"
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            In Preisliste übernehmen
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full border-collapse">
          <thead className="bg-orange-100 sticky top-0">
            <tr>
              <th className="border border-orange-200 px-3 py-2 text-left text-xs font-semibold text-orange-900">
                LV-Nr
              </th>
              <th className="border border-orange-200 px-3 py-2 text-left text-xs font-semibold text-orange-900">
                Beschreibung
              </th>
              <th className="border border-orange-200 px-3 py-2 text-left text-xs font-semibold text-orange-900">
                Volltext
              </th>
              <th className="border border-orange-200 px-3 py-2 text-center text-xs font-semibold text-orange-900">
                Einheit
              </th>
              <th className="border border-orange-200 px-3 py-2 text-right text-xs font-semibold text-orange-900">
                Menge
              </th>
              <th className="border border-orange-200 px-3 py-2 text-right text-xs font-semibold text-orange-900">
                EP
              </th>
              <th className="border border-orange-200 px-3 py-2 text-right text-xs font-semibold text-orange-900">
                GP
              </th>
              <th className="border border-orange-200 px-3 py-2 text-left text-xs font-semibold text-orange-900">
                Kommentare (Änderungen)
              </th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Sort positions by positionNumber for correct hierarchy display
              const sortByPositionNumber = (a: Position, b: Position) => {
                const aPos = (a as unknown as GAEBEntry).positionNumber || '';
                const bPos = (b as unknown as GAEBEntry).positionNumber || '';

                // Split into parts and compare numerically
                const aParts = aPos.split('.').map(p => parseInt(p) || 0);
                const bParts = bPos.split('.').map(p => parseInt(p) || 0);

                for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
                  const aVal = aParts[i] || 0;
                  const bVal = bParts[i] || 0;
                  if (aVal !== bVal) return aVal - bVal;
                }
                return 0;
              };

              // Sort all positions by their positionNumber
              const sortedPositions = [...positions].sort(sortByPositionNumber);
              const rows: JSX.Element[] = [];

              // Track group subtotals
              let currentGroupTitle: string | null = null;
              let currentGroupNumber: string | null = null;
              let currentGroupTotal = 0;
              let currentGroupItemCount = 0;

              // Helper to push a subtotal row for the current group
              const pushSubtotalRow = () => {
                if (currentGroupItemCount > 0 && currentGroupTitle) {
                  rows.push(
                    <tr key={`subtotal-${currentGroupNumber}`} className="bg-orange-50 border-b border-orange-200">
                      <td colSpan={6} className="px-3 py-1.5 text-right text-sm font-semibold text-orange-800">
                        Summe {currentGroupTitle}
                      </td>
                      <td className="px-3 py-1.5 text-right text-sm font-semibold text-orange-800 whitespace-nowrap">
                        {currentGroupTotal.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </td>
                      <td></td>
                    </tr>
                  );
                }
              };

              sortedPositions.forEach(position => {
                  const gaebEntry = position as unknown as GAEBEntry;
                  const isTitle = gaebEntry.type === 'title';
                  const id = position.id;
                  const lineSummary = gaebSummary.linesById.get(String(id));
                  const quantity = lineSummary?.quantity ?? getEffectiveQuantity(gaebEntry);
                  const displayTotal = lineSummary?.effectiveTotal ?? 0;

                  // Titles are displayed as full-width header rows
                  if (isTitle) {
                    // Push subtotal for previous group before starting new title
                    pushSubtotalRow();

                    // Reset group tracking for new title
                    currentGroupTitle = gaebEntry.shortText || null;
                    currentGroupNumber = gaebEntry.positionNumber || null;
                    currentGroupTotal = 0;
                    currentGroupItemCount = 0;

                    // Determine title style based on hierarchy level
                    const hierarchyLevel = gaebEntry.hierarchyLevel || 0;
                    const isMainTitle = hierarchyLevel === 0;
                    const titleStyle = isMainTitle
                      ? 'bg-orange-200 border-t-2 border-orange-400 text-lg'
                      : 'bg-orange-100 border-t border-orange-300 text-base';

                    rows.push(
                      <tr key={id} className={titleStyle}>
                        <td colSpan={8} className="px-3 py-2 font-bold text-orange-900">
                          <span className="mr-2">{gaebEntry.positionNumber}</span>
                          {gaebEntry.shortText}
                        </td>
                      </tr>
                    );
                    return;
                  }

                  // Track item in current group subtotal
                  const isBedarfs = isBedarfsposition(gaebEntry);
                  if (!lineSummary?.excludedFromMain) {
                    currentGroupTotal += displayTotal;
                  }
                  currentGroupItemCount++;

                  // Items are displayed as regular rows
                  // Bedarfspositionen get distinct styling to show they're excluded from main sum
                  rows.push(
                    <tr
                      key={id}
                      className={`border-b transition-colors ${
                        isBedarfs
                          ? 'bg-amber-50 hover:bg-amber-100 border-l-4 border-l-amber-400'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Position Number */}
                      <td className={`border border-orange-100 px-3 py-2 text-sm font-medium ${getFieldClassName(id, 'positionNumber', false)}`}>
                        <div className="flex items-center gap-1">
                          {gaebEntry.positionNumber}
                          {isBedarfs && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <Badge variant="outline" className="text-[10px] px-1 py-0 bg-amber-200 text-amber-800 border-amber-400 font-bold cursor-help">
                                  B
                                </Badge>
                              </PopoverTrigger>
                              <PopoverContent className="w-72 text-sm" side="right">
                                <div className="space-y-2">
                                  <p className="font-bold text-amber-800">Bedarfsposition (Optional)</p>
                                  <p className="text-gray-600">
                                    Diese Position ist <strong>optional</strong> und wird möglicherweise nicht beauftragt.
                                  </p>
                                  <p className="text-gray-600">
                                    Der Betrag ist <strong>nicht in der Hauptsumme</strong> enthalten, sondern wird separat ausgewiesen.
                                  </p>
                                  <p className="text-xs text-gray-500 border-t pt-2 mt-2">
                                    GAEB-Standard: &lt;Provis&gt;WithoutTotal&lt;/Provis&gt;
                                  </p>
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                          {needsBidderInput(gaebEntry) && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] px-1 py-0 cursor-help ${
                                    hasUserComment(gaebEntry)
                                      ? 'bg-green-100 text-green-700 border-green-300'
                                      : 'bg-blue-100 text-blue-700 border-blue-300'
                                  }`}
                                >
                                  {hasUserComment(gaebEntry) ? '✓' : 'T'}
                                </Badge>
                              </PopoverTrigger>
                              <PopoverContent className="w-80 text-sm" side="right">
                                <div className="space-y-2">
                                  <p className={`font-bold ${hasUserComment(gaebEntry) ? 'text-green-700' : 'text-blue-700'}`}>
                                    {hasUserComment(gaebEntry) ? 'Textergänzung vorhanden ✓' : 'Textergänzung erwartet'}
                                  </p>
                                  <p className="text-gray-600">
                                    Der Ausschreiber erwartet hier eine <strong>Angabe vom Bieter</strong> (z.B. Fabrikat, Typ, technische Details).
                                  </p>
                                  {hasUserComment(gaebEntry) ? (
                                    <p className="text-green-600 text-xs">
                                      Sie haben bereits einen Kommentar eingegeben. Dieser wird beim X84/X85-Export als Textergänzung übermittelt.
                                    </p>
                                  ) : (
                                    <p className="text-blue-600 text-xs">
                                      Bitte tragen Sie im Kommentarfeld rechts Ihre Angaben ein. Diese werden beim Export als Textergänzung übermittelt.
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-500 border-t pt-2 mt-2">
                                    GAEB-Standard: &lt;TextComplement Kind="Bidder"&gt;
                                  </p>
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                      </td>

                      {/* Description */}
                      <td className={`border border-orange-100 px-3 py-2 text-sm max-w-md ${getFieldClassName(id, 'shortText', false)}`}>
                        <p className="font-medium">{gaebEntry.shortText}</p>
                      </td>

                      {/* Volltext */}
                      <td className={`border border-orange-100 px-3 py-2 text-sm max-w-lg ${getFieldClassName(id, 'longText', false)}`}>
                        {gaebEntry.longText ? (
                          <div className="flex items-start gap-1">
                            <p className="text-xs text-gray-600 leading-relaxed line-clamp-3 flex-1">
                              {filterLongText(gaebEntry.longText)}
                            </p>
                            {gaebEntry.longText.length > 100 && (
                              <TextEditModal
                                value={filterLongText(gaebEntry.longText)}
                                onChange={() => {}} // Read-only for GAEB texts
                                title={`Volltext - ${gaebEntry.positionNumber || ''}`}
                                alwaysShowButton={true}
                                trigger={
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0 text-gray-400 hover:text-orange-600 flex-shrink-0"
                                    title="Volltext anzeigen"
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                  </Button>
                                }
                              />
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>

                      {/* Unit */}
                      <td className={`border border-orange-100 px-3 py-2 text-sm text-center ${getFieldClassName(id, 'einheit', false)}`}>
                        {gaebEntry.einheit}
                      </td>

                      {/* Menge (Quantity) - Editable only in X85 mode, locked in X84 mode */}
                      <td className={`border border-orange-100 px-3 py-2 text-sm text-right ${getFieldClassName(id, 'adjustedQuantity', false)}`}>
                        {editMode === 'x84' ? (
                          // X84 mode: quantities are READ-ONLY
                          <div className="h-8 flex items-center justify-end px-2 bg-gray-50 text-gray-600 rounded border border-gray-200">
                            <Lock className="h-3 w-3 mr-1 text-gray-400" />
                            {getEffectiveQuantity(gaebEntry)}
                          </div>
                        ) : (
                          // X85 mode: quantities are EDITABLE
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={localQuantityInputs[id] !== undefined ? localQuantityInputs[id] : getEffectiveQuantity(gaebEntry)}
                            onChange={(e) => {
                              // Store raw input value for proper decimal typing (comma or period)
                              const rawValue = e.target.value;
                              // Allow: digits, comma, period, empty
                              if (rawValue === '' || /^[\d.,]*$/.test(rawValue)) {
                                setLocalQuantityInputs(prev => ({ ...prev, [id]: rawValue }));
                              }
                            }}
                            onBlur={(e) => {
                              // On blur, parse and save the value
                              const rawValue = e.target.value.replace(',', '.');
                              if (rawValue === '') {
                                handleQuantityChange(id, undefined);
                              } else {
                                const parsed = parseFloat(rawValue);
                                if (!isNaN(parsed)) {
                                  handleQuantityChange(id, parsed);
                                }
                              }
                              // Clear local input state after blur
                              setLocalQuantityInputs(prev => {
                                const copy = { ...prev };
                                delete copy[id];
                                return copy;
                              });
                            }}
                            placeholder={String(quantity)}
                            className={`h-8 text-sm border-amber-300 focus:border-amber-400 text-right ${
                              isFieldModified(id, 'adjustedQuantity') ? 'bg-amber-50 border-amber-400' : ''
                            }`}
                          />
                        )}
                      </td>

                      {/* EP (Unit Price) - Editable, shows ourPrice or originalPrice */}
                      <td className={`border border-orange-100 px-3 py-2 ${getFieldClassName(id, 'ourPrice', false)}`}>
                        <Input
                          type="text"
                          inputMode="decimal"
                          data-price-input={gaebEntry.positionNumber}
                          value={localPriceInputs[id] !== undefined ? localPriceInputs[id] : (gaebEntry.ourPrice ?? '')}
                          onChange={(e) => {
                            // Store raw input value for proper decimal typing (comma or period)
                            const rawValue = e.target.value;
                            // Allow: digits, comma, period, empty
                            if (rawValue === '' || /^[\d.,]*$/.test(rawValue)) {
                              setLocalPriceInputs(prev => ({ ...prev, [id]: rawValue }));
                            }
                          }}
                          onBlur={(e) => {
                            // On blur, parse and save the value
                            const rawValue = e.target.value.replace(',', '.');
                            if (rawValue === '') {
                              handlePriceChange(id, undefined);
                            } else {
                              const parsed = parseFloat(rawValue);
                              if (!isNaN(parsed)) {
                                handlePriceChange(id, parsed);
                              }
                            }
                            // Clear local input state after blur
                            setLocalPriceInputs(prev => {
                              const copy = { ...prev };
                              delete copy[id];
                              return copy;
                            });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              // Trigger blur to save value
                              (e.target as HTMLInputElement).blur();
                              // Find all price inputs and get the next one
                              setTimeout(() => {
                                const allPriceInputs = Array.from(
                                  document.querySelectorAll<HTMLInputElement>('[data-price-input]')
                                );
                                const currentIndex = allPriceInputs.findIndex(
                                  input => input.dataset.priceInput === gaebEntry.positionNumber
                                );
                                if (currentIndex !== -1 && currentIndex < allPriceInputs.length - 1) {
                                  allPriceInputs[currentIndex + 1].focus();
                                  allPriceInputs[currentIndex + 1].select();
                                }
                              }, 10);
                            }
                          }}
                          placeholder={gaebEntry.originalPrice ? formatCurrency(gaebEntry.originalPrice) : "Preis eingeben"}
                          className={`h-8 text-sm border-orange-300 focus:border-orange-400 text-right ${
                            isFieldModified(id, 'ourPrice') ? 'bg-red-50 border-red-400' : ''
                          }`}
                        />
                      </td>

                      {/* GP (Total Price) - Calculated from displayPrice * quantity */}
                      {/* Bedarfspositionen: show in parentheses with amber color to indicate exclusion from main sum */}
                      <td className={`border border-orange-100 px-3 py-2 text-sm text-right font-medium ${getFieldClassName(id, 'ourPrice', false)}`}>
                        {displayTotal > 0 ? (
                          isBedarfs ? (
                            <Popover>
                              <PopoverTrigger asChild>
                                <span className="text-amber-600 cursor-help">
                                  ({formatCurrency(displayTotal)})
                                </span>
                              </PopoverTrigger>
                              <PopoverContent className="w-64 text-sm" side="left">
                                <p className="font-bold text-amber-800 mb-2">Bedarfsposition</p>
                                <p className="text-gray-600">
                                  Dieser Betrag ist <strong>nicht in der Hauptsumme</strong> enthalten.
                                </p>
                                <p className="text-xs text-gray-500 mt-2">
                                  Die Klammern zeigen an, dass diese Position optional ist.
                                </p>
                              </PopoverContent>
                            </Popover>
                          ) : (
                            formatCurrency(displayTotal)
                          )
                        ) : ''}
                      </td>

                      {/* Comments (Red text field - auto-expanding) */}
                      <td className={`border border-orange-100 px-3 py-2 ${getFieldClassName(id, 'ourComment', false)}`}>
                        <Textarea
                          value={gaebEntry.ourComment ?? ''}
                          onChange={(e) => handleCommentChange(id, e.target.value || undefined)}
                          placeholder="Änderungen notieren..."
                          rows={1}
                          className={`min-h-[32px] text-sm border-orange-300 focus:border-orange-400 resize-none overflow-hidden ${
                            isFieldModified(id, 'ourComment')
                              ? 'bg-red-50 text-red-600 border-red-400'
                              : ''
                          }`}
                          style={{ height: 'auto' }}
                          onInput={(e) => {
                            const target = e.target as HTMLTextAreaElement;
                            target.style.height = 'auto';
                            target.style.height = `${Math.max(32, target.scrollHeight)}px`;
                          }}
                          onFocus={(e) => {
                            const target = e.target as HTMLTextAreaElement;
                            target.style.height = 'auto';
                            target.style.height = `${Math.max(32, target.scrollHeight)}px`;
                          }}
                        />
                      </td>
                    </tr>
                  );
                });

              // Push subtotal for the last group
              pushSubtotalRow();

              const grandTotal = gaebSummary.mainTotal;
              const bedarfsTotal = gaebSummary.bedarfTotal;

              // Show the canonical main sum, including a deliberately priced 0 EUR offer.
              if (gaebSummary.totalItemCount > 0) {
                rows.push(
                  <tr key="total-summary" className="bg-green-50 border-t-4 border-green-300">
                    <td colSpan={6} className="border border-green-100 px-3 py-3 text-lg font-bold text-green-900 text-right">
                      <Popover>
                        <PopoverTrigger asChild>
                          <span className="cursor-help underline decoration-dotted">
                            GESAMTSUMME (netto):
                          </span>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 text-sm" side="left">
                          <p className="font-bold text-green-800 mb-2">Hauptsumme ohne Bedarfs- und Alternativpositionen</p>
                          <p className="text-gray-600">
                            Diese Summe enthält <strong>nur die fest beauftragten</strong> Positionen.
                          </p>
                          <p className="text-gray-600 mt-2">
                            Bedarfs- und Alternativpositionen sind <strong>nicht enthalten</strong>; Bedarfspositionen werden separat ausgewiesen.
                          </p>
                        </PopoverContent>
                      </Popover>
                    </td>
                    <td className="border border-green-100 px-3 py-3 text-lg font-bold text-green-900 text-right">
                      {formatCurrency(grandTotal)}
                    </td>
                    <td className="border border-green-100 px-3 py-3"></td>
                  </tr>
                );
              }

              // Show Bedarfspositionen (optional positions) sum separately if any exist
              if (bedarfsTotal > 0) {
                rows.push(
                  <tr key="bedarfs-summary" className="bg-amber-100 border-l-4 border-l-amber-400">
                    <td colSpan={6} className="border border-amber-200 px-3 py-2 text-base font-bold text-amber-900 text-right">
                      <Popover>
                        <PopoverTrigger asChild>
                          <span className="flex items-center justify-end gap-2 cursor-help">
                            <Badge variant="outline" className="bg-amber-200 text-amber-800 border-amber-400 font-bold">
                              B
                            </Badge>
                            <span className="underline decoration-dotted">
                              zzgl. Bedarfspositionen (optional):
                            </span>
                          </span>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 text-sm" side="left">
                          <p className="font-bold text-amber-800 mb-2">Bedarfspositionen (optional)</p>
                          <p className="text-gray-600">
                            Diese Positionen sind <strong>optional</strong> und werden nur bei Bedarf beauftragt.
                          </p>
                          <p className="text-gray-600 mt-2">
                            Der Betrag ist <strong>nicht in der Hauptsumme</strong> oben enthalten.
                          </p>
                          <p className="text-xs text-gray-500 border-t pt-2 mt-2">
                            Im GAEB-Export werden diese mit &lt;Provis&gt;WithoutTotal&lt;/Provis&gt; gekennzeichnet.
                          </p>
                        </PopoverContent>
                      </Popover>
                    </td>
                    <td className="border border-amber-200 px-3 py-2 text-base font-bold text-amber-900 text-right">
                      ({formatCurrency(bedarfsTotal)})
                    </td>
                    <td className="border border-amber-200 px-3 py-2"></td>
                  </tr>
                );

                // Show combined total (main + Bedarfs) for reference
                rows.push(
                  <tr key="combined-summary" className="bg-gray-100 border-t border-gray-300">
                    <td colSpan={6} className="border border-gray-200 px-3 py-2 text-sm text-gray-700 text-right">
                      <Popover>
                        <PopoverTrigger asChild>
                          <span className="cursor-help underline decoration-dotted">
                            Gesamt inkl. Bedarfspositionen:
                          </span>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 text-sm" side="left">
                          <p className="font-bold text-gray-800 mb-2">Maximale Auftragssumme</p>
                          <p className="text-gray-600">
                            Diese Summe zeigt den <strong>maximalen Auftragswert</strong>, falls alle Bedarfspositionen beauftragt werden.
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            = Hauptsumme + Bedarfspositionen
                          </p>
                        </PopoverContent>
                      </Popover>
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 text-right">
                      {formatCurrency(gaebSummary.combinedMainAndBedarfTotal)}
                    </td>
                    <td className="border border-gray-200 px-3 py-2"></td>
                  </tr>
                );
              }

              return rows;
            })()}
          </tbody>
        </table>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-7 gap-4 bg-gray-50 p-4 rounded-lg">
        <div className="text-center">
          <div className="text-2xl font-bold text-orange-600">
            {gaebSummary.totalItemCount}
          </div>
          <p className="text-xs text-gray-600">Positionen</p>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">
            {gaebSummary.pricedItemCount}
          </div>
          <p className="text-xs text-gray-600">Mit Preis</p>
        </div>
        <div className="text-center">
          {(() => {
            const needingInput = positions.filter(p => needsBidderInput(p as unknown as GAEBEntry));
            const withComment = needingInput.filter(p => hasUserComment(p as unknown as GAEBEntry));
            return (
              <>
                <div className={`text-2xl font-bold ${withComment.length === needingInput.length ? 'text-green-600' : 'text-blue-600'}`}>
                  {withComment.length}/{needingInput.length}
                </div>
                <p className="text-xs text-gray-600">Textergänzungen</p>
              </>
            );
          })()}
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(gaebSummary.mainTotal)}
          </div>
          <p className="text-xs text-gray-600">Unser Gesamt</p>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-600">
            {formatCurrency(gaebSummary.originalMainTotal)}
          </div>
          <p className="text-xs text-gray-600">Original Gesamt</p>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">
            {Object.keys(modifiedFields).length}
          </div>
          <p className="text-xs text-gray-600">Geänderte Einträge</p>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">
            {completionRate}%
          </div>
          <p className="text-xs text-gray-600">Fertigstellung</p>
        </div>
      </div>

      {/* Gewerk Summary Table - Below all positions */}
      <div className="space-y-4 bg-gradient-to-b from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg p-6">
        <h3 className="text-lg font-bold text-purple-900 mb-4">Zusammenfassung nach Gewerk</h3>
        
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-purple-200">
              <th className="border border-purple-300 px-4 py-2 text-left text-sm font-bold text-purple-900">
                Gewerk
              </th>
              <th className="border border-purple-300 px-4 py-2 text-right text-sm font-bold text-purple-900">
                Positionen
              </th>
              <th className="border border-purple-300 px-4 py-2 text-right text-sm font-bold text-purple-900">
                Original Summe
              </th>
              <th className="border border-purple-300 px-4 py-2 text-right text-sm font-bold text-purple-900">
                Unsere Summe
              </th>
              <th className="border border-purple-300 px-4 py-2 text-right text-sm font-bold text-purple-900">
                Differenz
              </th>
            </tr>
          </thead>
          <tbody>
            {gaebSummary.groups.map((summary, idx) => {
                const difference = summary.effectiveTotal - summary.originalTotal;
                const diffPercentage = summary.originalTotal > 0 
                  ? ((difference / summary.originalTotal) * 100).toFixed(1)
                  : '0.0';

                const gewerkKey = `gewerk-${idx}`;
                const editableName = editableGewerkNames[gewerkKey] || summary.name;
                const isEditing = editingGewerkKey === gewerkKey;

                return (
                  <tr key={`gewerk-summary-${idx}`} className="hover:bg-purple-100 transition-colors">
                    {/* Editable Gewerk Name */}
                    <td 
                      className="border border-purple-200 px-4 py-3 text-sm font-medium text-purple-900 cursor-pointer hover:bg-purple-50"
                      onDoubleClick={() => setEditingGewerkKey(gewerkKey)}
                    >
                      {isEditing ? (
                        <Input
                          autoFocus
                          value={editableName}
                          onChange={(e) => setEditableGewerkNames(prev => ({
                            ...prev,
                            [gewerkKey]: e.target.value
                          }))}
                          onBlur={() => setEditingGewerkKey(null)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') setEditingGewerkKey(null);
                            if (e.key === 'Escape') {
                              setEditableGewerkNames(prev => {
                                const copy = { ...prev };
                                delete copy[gewerkKey];
                                return copy;
                              });
                              setEditingGewerkKey(null);
                            }
                          }}
                          className="h-8 text-sm px-2"
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <span>{editableName}</span>
                          <span className="text-xs text-purple-400">(double-click to edit)</span>
                        </div>
                      )}
                    </td>
                    <td className="border border-purple-200 px-4 py-3 text-sm text-right text-purple-700">
                      {summary.count}
                    </td>
                    <td className="border border-purple-200 px-4 py-3 text-sm text-right font-medium text-gray-700">
                      {formatCurrency(summary.originalTotal)}
                    </td>
                    <td className="border border-purple-200 px-4 py-3 text-sm text-right font-bold text-green-700">
                      {formatCurrency(summary.effectiveTotal)}
                    </td>
                    <td className={`border border-purple-200 px-4 py-3 text-sm text-right font-semibold ${
                      difference > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {formatCurrency(difference)} ({difference > 0 ? '+' : ''}{diffPercentage}%)
                    </td>
                  </tr>
                );
              })}
          </tbody>
          <tfoot>
            <tr className="bg-purple-300 font-bold border-t-2 border-purple-400">
              <td className="border border-purple-400 px-4 py-3 text-sm text-purple-900">
                GESAMTBUDGET
              </td>
              <td className="border border-purple-400 px-4 py-3 text-sm text-right text-purple-900">
                {gaebSummary.totalItemCount}
              </td>
              <td className="border border-purple-400 px-4 py-3 text-sm text-right text-purple-900">
                {formatCurrency(gaebSummary.originalMainTotal)}
              </td>
              <td className="border border-purple-400 px-4 py-3 text-sm text-right text-purple-900">
                {formatCurrency(gaebSummary.mainTotal)}
              </td>
              <td className="border border-purple-400 px-4 py-3 text-sm text-right text-purple-900">
                {(() => {
                  const diff = gaebSummary.mainTotal - gaebSummary.originalMainTotal;
                  const pct = gaebSummary.originalMainTotal > 0
                    ? ((diff / gaebSummary.originalMainTotal) * 100).toFixed(1)
                    : '0.0';
                  return `${formatCurrency(diff)} (${diff > 0 ? '+' : ''}${pct}%)`;
                })()}
              </td>
            </tr>
          </tfoot>
        </table>

        <div className={`text-xs p-3 rounded border ${
          editMode === 'x84'
            ? 'text-blue-700 bg-blue-100 border-blue-200'
            : 'text-amber-700 bg-amber-100 border-amber-200'
        }`}>
          {editMode === 'x84' ? (
            <>
              🔒 <strong>X84-Modus (Standard):</strong> Mengen sind gesperrt und werden aus dem Original-LV übernommen.
              Nur Preise können eingegeben werden. Für Mengenänderungen wechseln Sie zu X85.
            </>
          ) : (
            <>
              🔓 <strong>X85-Modus:</strong> Mengen können pro Position angepasst werden.
              Alle Berechnungen werden automatisch aktualisiert. Mengenänderungen werden in den Kommentaren dokumentiert.
            </>
          )}
        </div>
      </div>

      {/* Export Section */}
      <div className={`flex items-center justify-between border rounded-lg p-4 ${
        editMode === 'x84' ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'
      }`}>
        <div>
          <h3 className={`font-medium ${editMode === 'x84' ? 'text-blue-900' : 'text-amber-900'}`}>
            DA XML Export ({editMode.toUpperCase()})
          </h3>
          <p className={`text-sm ${editMode === 'x84' ? 'text-blue-700' : 'text-amber-700'}`}>
            {editMode === 'x84'
              ? 'Preisabgabe exportieren (Mengen unverändert)'
              : 'Alternativabgabe mit Mengenänderungen/Kommentaren'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* PDF Export with options */}
          <div className="flex items-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPDF}
              disabled={isExportingPDF}
              className="text-blue-600 border-blue-300 hover:bg-blue-50 rounded-r-none border-r-0"
            >
              {isExportingPDF ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-1" />
              )}
              PDF
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-blue-600 border-blue-300 hover:bg-blue-50 rounded-l-none px-1.5"
                  disabled={isExportingPDF}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="end">
                <div className="space-y-3">
                  <p className="text-xs font-medium text-gray-700">PDF Optionen</p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="pdf-comments-2"
                        checked={pdfOptions.includeComments}
                        onCheckedChange={(checked) => updatePdfOption('includeComments', !!checked)}
                      />
                      <Label htmlFor="pdf-comments-2" className="text-xs cursor-pointer">
                        Kommentare (rot)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="pdf-prices-2"
                        checked={pdfOptions.showPrices}
                        onCheckedChange={(checked) => updatePdfOption('showPrices', !!checked)}
                      />
                      <Label htmlFor="pdf-prices-2" className="text-xs cursor-pointer">
                        Preise anzeigen
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="pdf-quantities-2"
                        checked={pdfOptions.showQuantities}
                        onCheckedChange={(checked) => updatePdfOption('showQuantities', !!checked)}
                      />
                      <Label htmlFor="pdf-quantities-2" className="text-xs cursor-pointer">
                        Mengen anzeigen
                      </Label>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <GAEBExport defaultFormat={editMode} />
        </div>
      </div>

      {/* Mode Switch Warning Dialog */}
      <AlertDialog open={showModeWarning} onOpenChange={setShowModeWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Wechsel zu X84-Modus
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Sie wechseln in den X84-Modus (Standard-Preisabgabe).
                  Im X84-Format werden folgende Änderungen <strong>NICHT übertragen</strong>:
                </p>
                <ul className="list-disc list-inside space-y-1 text-amber-700 bg-amber-50 p-3 rounded border border-amber-200">
                  {modificationsInfo.hasChangedQuantities && (
                    <li>
                      <strong>{modificationsInfo.changedQuantitiesCount}</strong> Mengenänderung(en)
                      {modificationsInfo.changedQuantityPositions.length > 0 && (
                        <span className="text-xs text-amber-600 ml-1">
                          (z.B. {modificationsInfo.changedQuantityPositions.join(', ')})
                        </span>
                      )}
                    </li>
                  )}
                  {modificationsInfo.hasComments && (
                    <li><strong>{modificationsInfo.commentsCount}</strong> Kommentar(e)</li>
                  )}
                </ul>
                <p className="text-sm text-gray-600">
                  Die Änderungen bleiben gespeichert, werden aber beim X84-Export ignoriert.
                  Sie können jederzeit zurück zu X85 wechseln.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowModeWarning(false);
              setPendingModeSwitch(null);
            }}>
              Abbrechen (X85 beibehalten)
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingModeSwitch && confirmModeSwitch(pendingModeSwitch)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Zu X84 wechseln
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
